import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { api } from "@/lib/api";
import { BRAND } from "@/config/brand";
import { MessageCircle, X, Send, Mail, Sparkles } from "lucide-react";
import { toast } from "sonner";

const ADMIN_EMAIL = BRAND.supportEmail;

function sessionKey() {
  const KEY = "mooly_session";
  let s = localStorage.getItem(KEY);
  if (!s) {
    s = `mooly-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    localStorage.setItem(KEY, s);
  }
  return s;
}

export default function MoolyWidget() {
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [msgs, setMsgs] = useState([
    { role: "assistant",
      content: `Hi — I'm Mooly ✨ I help with anything about ${BRAND.name}. Ask about services, pricing, counselors, or booking. If you need a human, tap "Contact admin" to email us directly.` },
  ]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const listRef = useRef(null);
  const sid = useRef(sessionKey());

  useEffect(() => {
    if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight;
  }, [msgs, open]);

  // Mooly has one dedicated, stable spot (bottom-right) everywhere in the
  // app EXCEPT on the live video-call page, where it's hidden outright so
  // it can never sit over the call's own controls (mute/camera/Join, etc).
  const onVideoCallPage = /^\/session\//.test(location.pathname);
  if (onVideoCallPage) return null;

  const send = async () => {
    const text = input.trim();
    if (!text || busy) return;
    setInput("");
    setMsgs(m => [...m, { role: "user", content: text }]);
    setBusy(true);
    try {
      const { data } = await api.post("/mooly/chat", { session_id: sid.current, message: text });
      setMsgs(m => [...m, { role: "assistant", content: data.reply }]);
    } catch (err) {
      setMsgs(m => [...m, { role: "assistant", content: "Sorry, I hit a snag. You can try again or tap \"Contact admin\" to email us." }]);
    } finally { setBusy(false); }
  };

  const contactAdmin = () => {
    // Include the last few chat turns as email body for context
    const transcript = msgs.slice(-8).map(m => {
      const who = m.role === "user" ? "Me" : "Mooly";
      return `${who}: ${m.content}`;
    }).join("\n\n");
    const subject = `${BRAND.shortName} support request`;
    const body = `Hi ${BRAND.name} team,

I was chatting with Mooly and would like to talk to the admin.

Recent conversation with Mooly:
------------------------------
${transcript || "(no chat yet)"}
------------------------------

Please help with:
`;
    const mailto = `mailto:${ADMIN_EMAIL}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    // fire escalation ticket in background too (best-effort, non-blocking)
    api.post("/mooly/escalate", {
      session_id: sid.current,
      subject: "Email opened to admin",
      summary: transcript || null,
    }).catch(() => {});
    window.location.href = mailto;
    toast.success("Opening your email app…");
  };

  return (
    <>
      {!open && (
        <div className="mooly-fab-ring fixed bottom-6 right-6 z-50">
          <button data-testid="mooly-open" onClick={() => setOpen(true)}
            className="rounded-full pl-4 pr-5 py-3 flex items-center gap-2 transition-transform hover:-translate-y-0.5"
            style={{background:"var(--brand)", color:"#fff"}}>
            <MessageCircle className="w-5 h-5"/>
            <span className="font-medium">Ask Mooly</span>
            <Sparkles className="w-3.5 h-3.5" style={{color:"#F8D8B6"}}/>
          </button>
        </div>
      )}

      {open && (
        <div data-testid="mooly-panel" className="fixed bottom-6 right-6 z-50 w-[92vw] max-w-sm h-[70vh] max-h-[560px] flex flex-col rounded-2xl shadow-2xl overflow-hidden"
          style={{background:"#fff", border:"1px solid var(--border-soft)"}}>
          <div className="flex items-center justify-between px-4 py-3 border-b" style={{borderColor:"var(--border-soft)"}}>
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-full flex items-center justify-center" style={{background:"var(--brand)"}}>
                <Sparkles className="w-4 h-4" style={{color:"#F8D8B6"}}/>
              </div>
              <div>
                <div className="font-serif-display text-lg leading-none">Mooly</div>
                <div className="text-[0.65rem] uppercase tracking-widest" style={{color:"var(--text-secondary)"}}>Assistant</div>
              </div>
            </div>
            <button data-testid="mooly-close" onClick={() => setOpen(false)} className="text-[color:var(--text-secondary)] hover:text-[color:var(--text-primary)]">
              <X className="w-5 h-5"/>
            </button>
          </div>

          <div ref={listRef} className="flex-1 overflow-y-auto p-4 space-y-3" data-testid="mooly-messages" style={{background:"var(--bg-main)"}}>
            {msgs.map((m, i) => (
              <div key={`m-${i}-${m.role}-${m.content.length}`} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div className="rounded-2xl px-3 py-2 text-sm max-w-[85%] whitespace-pre-wrap leading-relaxed"
                  style={m.role === "user"
                    ? {background:"var(--brand)", color:"#fff"}
                    : {background:"#fff", border:"1px solid var(--border-soft)"}}>
                  {m.content}
                </div>
              </div>
            ))}
            {busy && <div className="text-xs" style={{color:"var(--text-secondary)"}}>Mooly is typing…</div>}
          </div>

          <div className="px-3 pt-2 pb-1 flex justify-end items-center border-t" style={{borderColor:"var(--border-soft)"}}>
            <button data-testid="mooly-contact-admin" onClick={contactAdmin}
              className="text-xs inline-flex items-center gap-1 underline"
              style={{color:"var(--accent)"}}>
              <Mail className="w-3.5 h-3.5"/> Contact admin
            </button>
          </div>
          <div className="p-3 flex gap-2">
            <input data-testid="mooly-input" value={input} onChange={e=>setInput(e.target.value)}
              onKeyDown={e=>{ if (e.key === "Enter") send(); }}
              placeholder={`Ask me anything about ${BRAND.shortName}…`}
              className="flex-1 rounded-full border border-[color:var(--border-soft)] px-4 py-2 text-sm focus:outline-none focus:border-[color:var(--brand)]"/>
            <button data-testid="mooly-send" disabled={busy || !input.trim()} onClick={send}
              className="rounded-full w-10 h-10 flex items-center justify-center disabled:opacity-40"
              style={{background:"var(--brand)", color:"#fff"}}>
              <Send className="w-4 h-4"/>
            </button>
          </div>
        </div>
      )}
    </>
  );
}
